import type { TimeEntryDto } from '@tk/shared';
import { Ban, Check, CornerDownRight, Lock, Pencil, SquareKanban, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import type { ReactNode } from 'react';
import { useKaannos } from '../lib/i18n';
import AsiakasKoodi from './AsiakasKoodi';
import Dialogi from './Dialogi';

interface Props {
  entry: TimeEntryDto;
  locked: boolean;
  auki: boolean;
  onSulje: () => void;
  onMuokkaa: () => void;
  onPoista: () => void;
}

/**
 * Kirjauksen tarkastelu.
 *
 * Kalenterin kortti on tiivis: kuvaus katkeaa ja asiakkaasta näkyy vain
 * koodi. Tarkastelu näyttää saman kirjauksen kokonaan ilman, että sitä
 * pitää avata muokattavaksi — lukeminen ei ole muuttamista, eikä
 * tarkistava klikkaus saa altistaa tallennuspainikkeelle.
 *
 * Euromääriä ei näytetä täälläkään: kalenteri on kirjaajan näkymä, ja
 * hinnat kuuluvat raportteihin (suunnitelma 10).
 */
export default function KirjausTiedot({
  entry,
  locked,
  auki,
  onSulje,
  onMuokkaa,
  onPoista,
}: Props) {
  const { t, m } = useKaannos();

  return (
    <Dialogi
      otsikko={`${t('tiedot.otsikko')} · ${m.pvm(entry.entryDate)}`}
      auki={auki}
      onSulje={onSulje}
    >
      <dl className="space-y-3 text-sm">
        <Rivi nimike={t('yleinen.asiakas')}>
          <span className="flex items-center gap-1.5">
            <AsiakasKoodi code={entry.customer.code} colorKey={entry.customer.colorKey} />
            <span className="text-teksti-vaimea">{entry.customer.name}</span>
          </span>
        </Rivi>

        <Rivi nimike={t('kirjaus.tehtavalaji')}>{entry.taskType.name}</Rivi>

        {entry.task && (
          <Rivi nimike={t('kirjaus.tehtava')}>
            <Link
              to={`/taulut/tehtava/${entry.task.id}`}
              className="text-korostus flex items-center gap-1 hover:underline"
            >
              <SquareKanban className="size-3.5 shrink-0" aria-hidden="true" />
              {entry.task.title}
            </Link>
          </Rivi>
        )}

        {entry.project && (
          <Rivi nimike={t('kirjaus.projekti')}>
            <span className="flex items-center gap-1">
              <CornerDownRight
                className="text-teksti-vaimea size-3.5 shrink-0"
                aria-hidden="true"
              />
              {entry.project.name}
            </span>
          </Rivi>
        )}

        <Rivi nimike={t('kirjaus.kesto')}>
          <span className="luvut">{m.tunnit(entry.minutes)}</span>
        </Rivi>

        <Rivi nimike={t('yleinen.laskutettava')}>
          <span className="flex items-center gap-1.5">
            {entry.billable ? (
              <Check className="text-korostus size-4 shrink-0" aria-hidden="true" />
            ) : (
              <Ban className="text-teksti-vaimea size-4 shrink-0" aria-hidden="true" />
            )}
            {entry.billable ? t('yleinen.kylla') : t('yleinen.eiLaskuteta')}
          </span>
        </Rivi>

        <Rivi nimike={t('kirjaus.kuvaus')}>
          {entry.description ? (
            <span className="whitespace-pre-wrap">{entry.description}</span>
          ) : (
            <span className="text-teksti-vaimea italic">{t('tiedot.eiKuvausta')}</span>
          )}
        </Rivi>

        <Rivi nimike={t('tiedot.kirjaaja')}>{entry.user.name}</Rivi>
      </dl>

      <p className="text-teksti-vaimea border-reuna mt-4 border-t pt-3 text-xs">
        {t('tiedot.kirjattu')} {m.pvmAika(entry.createdAt)}
        {entry.updatedAt !== entry.createdAt && (
          <> · {`${t('tiedot.muokattu')} ${m.pvmAika(entry.updatedAt)}`}</>
        )}
      </p>

      {locked && (
        <p className="text-teksti-vaimea mt-3 flex items-center gap-1.5 text-xs">
          <Lock className="size-3.5 shrink-0" aria-hidden="true" />
          {t('tiedot.lukittu')}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onSulje}
          className="border-reuna text-teksti-vaimea hover:text-teksti rounded-md border px-4 py-2 text-sm"
        >
          {t('yleinen.sulje')}
        </button>
        {!locked && (
          <>
            <button
              type="button"
              onClick={onPoista}
              className="border-reuna text-teksti-vaimea hover:text-yli hover:border-yli flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm transition-colors"
            >
              <Trash2 className="size-4" aria-hidden="true" />
              {t('yleinen.poista')}
            </button>
            <button
              type="button"
              onClick={onMuokkaa}
              className="bg-brandi hover:bg-brandi-syva flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium text-white transition-colors"
            >
              <Pencil className="size-4" aria-hidden="true" />
              {t('yleinen.muokkaa')}
            </button>
          </>
        )}
      </div>
    </Dialogi>
  );
}

/** Nimike ja arvo samalla rivillä — nimike kapea, arvo saa loput. */
function Rivi({ nimike, children }: { nimike: string; children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="text-teksti-vaimea w-28 shrink-0">{nimike}</dt>
      <dd className="text-teksti min-w-0 flex-1 break-words">{children}</dd>
    </div>
  );
}
